import { useEffect, useState } from "react";
import {
  CheckCircle,
  ArrowRight,
  Globe,
  Award,
  BookOpen,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const EligibilityCheckBlock = () => {
  const { t } = useTranslation();
  const b = (k: string) => t(`eligibility.block.${k}`);
  const [totalChecks, setTotalChecks] = useState(5000);

  useEffect(() => {
    // Fetch current count on component mount
    const fetchCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/eligibility-check/count`,
          { credentials: "include" },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setTotalChecks(data.totalCount);
      } catch (error) {
        console.error("Failed to fetch eligibility check count:", error);
        // Keep default 5000 if fetch fails
      }
    };

    fetchCount();
  }, []);

  return (
    <section className="bg-gray-50 py-4 font-sans pb-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Main Card - Compact Version with Width Constraint */}
        <div className="relative bg-gradient-to-r from-[#009fe3] to-[#0088cc] rounded-md shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-3xl hover:-translate-y-1">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="relative flex flex-col lg:flex-row items-center gap-6 p-6 lg:p-10">
            {/* Left Content */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center space-y-4">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full w-fit">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">{b("badge")}</span>
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                  {b("title")}
                </h2>
                <p className="text-sm lg:text-base text-blue-50 leading-relaxed">
                  {b("subtitle")}
                </p>
              </div>

              {/* Features - Compact 2-column */}
              <div className="grid grid-cols-2 gap-2 py-2">
                {[
                  { icon: CheckCircle, text: b("feat1") },
                  { icon: Globe, text: b("feat2") },
                  { icon: Award, text: b("feat3") },
                  { icon: BookOpen, text: b("feat4") },
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white text-xs font-medium">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button - Compact */}
              <div className="pt-1">
                <a
                  href="/pages/check-eligibility"
                  className="w-full inline-block bg-white hover:bg-gray-50 text-[#009fe3] font-semibold text-base py-2.5 px-6 rounded-lg shadow transition duration-300 text-center"
                  aria-label="Check Your Eligibility"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {b("cta")}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </a>
              </div>
            </div>

            {/* Right Visual - Compact */}
            <div className="hidden lg:flex w-full lg:w-1/2 items-center justify-center">
              <div className="relative">
                {/* Main Circle - Smaller */}
                <div className="w-52 h-52 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/30 shadow-xl">
                  <div className="w-40 h-40 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/40">
                    <div className="text-center">
                      <CheckCircle className="w-12 h-12 text-white mx-auto mb-2" />
                      <p className="text-white font-bold text-base mb-0.5">
                        {b("circleTitle")}
                      </p>
                      <p className="text-blue-100 text-xs">{b("circleMin")}</p>
                    </div>
                  </div>
                </div>

                {/* Floating Cards - Smaller */}
                <div className="absolute -top-3 -right-3 bg-white rounded-lg p-2 shadow-lg animate-[float_3s_ease-in-out_infinite]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                      <BookOpen className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Ausbildung</p>
                      <p className="text-xs font-bold text-gray-900">
                        {b("eligible")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-3 -left-3 bg-white rounded-lg p-2 shadow-lg animate-[float_3s_ease-in-out_infinite_0.5s]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                      <Globe className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Masters</p>
                      <p className="text-xs font-bold text-gray-900">
                        {b("available")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats - Mobile & Desktop */}
          <div className="relative border-t border-white/20 bg-white/5 backdrop-blur-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
              {[
                { number: `${totalChecks.toLocaleString()}`, label: b("statChecks") },
                { number: "2 Min", label: b("statResults") },
                { number: "100%", label: b("statFree") },
                { number: "24/7", label: b("statAvailable") },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-lg sm:text-base lg:text-lg font-bold text-white mb-0.5">
                    {stat.number}
                  </p>
                  <p className="text-xs sm:text-[10px] lg:text-xs text-blue-100">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }
      `}</style>
    </section>
  );
};

export default EligibilityCheckBlock;
